import React, { useState } from 'react';
import { Button, Card, Divider, Modal, Typography } from 'antd';

import { ErrorRender } from '@partsbadger/library';
import * as api from '@partsbadger/utils';
import { notification } from 'antd';
import { PartDimensions } from '../PartDimensions';
import ThreeDViewerModal from '../ThreeDViewerModal';
import QuoteStore from '../../stores/QuoteStore';
import { CartItem } from './CartItem';
import { SummaryPartGroup } from './summaryGrouping';

const { Text } = Typography;

interface Props {
    group: SummaryPartGroup;
    partNumber: number;
    partCount: number;
    isAutoQuote: boolean;
    onEditPartSpecifications?: (masterProductId: number, lineItemId: number) => void;
}

export const CartItemGroup = ({ group, partNumber, partCount, isAutoQuote, onEditPartSpecifications }: Props) => {
    const [threeDUrl, setThreeDUrl] = useState<string | null>(null);
    const [threeDViewerVisible, setThreeDViewerVisible] = useState(false);
    const [threeDLoading, setThreeDLoading] = useState(false);
    const [confirmRemovePartVisible, setConfirmRemovePartVisible] = useState(false);
    const [removingPart, setRemovingPart] = useState(false);

    const { baseItem, variants } = group;
    const product = baseItem.product;
    const dimensions = product?.dimensions_dict;
    const file3dUrl = product?.attachments?.file3d?.file_3djs ?? null;
    const file2dUrl = product?.attachments?.files_2d?.file ?? null;
    const masterProductId = Number(product?.master_product);

    const handleRemovePart = async () => {
        setRemovingPart(true);
        try {
            await QuoteStore.deleteMasterProduct(masterProductId);
            setConfirmRemovePartVisible(false);
        } finally {
            setRemovingPart(false);
        }
    };

    const open3dViewer = async () => {
        setThreeDViewerVisible(true);
        if (threeDUrl) return;
        if (file3dUrl) {
            setThreeDUrl(file3dUrl);
            return;
        }

        setThreeDLoading(true);
        try {
            const data = await api.get(`/customer/quote/${QuoteStore.quote_uid}/threed-signed-url/${baseItem.uid}`);
            setThreeDUrl(data.url);
        } catch (error: any) {
            notification.error({
                message: 'Unable to open 3D viewer',
                description: <ErrorRender error={error} />,
            });
        } finally {
            setThreeDLoading(false);
        }
    };

    const open2dViewer = () => {
        if (!file2dUrl) {
            return;
        }

        const win = window.open(file2dUrl, '_blank', 'noopener,noreferrer,width=800,height=800,left=200,top=200');
        if (win) {
            win.opener = null;
        }
    };

    return (
        <Card
            size="small"
            className="otf-checkout-cart-item"
            title={
                <div className="otf-checkout-card-header-content">
                    <Text className="font-size-md">
                        <span className="font-bold">{product.name}</span>
                    </Text>
                    {dimensions?.length && dimensions?.width && dimensions?.height ? (
                        <span className="otf-checkout-card-dimensions">
                            <strong>Dimensions:</strong> <PartDimensions {...dimensions} />
                        </span>
                    ) : null}
                    {dimensions?.length && dimensions?.width && dimensions?.height && (product?.has_file_3d || file2dUrl) ? (
                        <Divider type="vertical" />
                    ) : null}
                    {product?.has_file_3d || file2dUrl ? (
                        <div className="otf-checkout-card-file-actions">
                            {product?.has_file_3d ? (
                                <Button onClick={open3dViewer} size="small" type="link">
                                    View 3D
                                </Button>
                            ) : null}
                            {product?.has_file_3d && file2dUrl ? <Divider type="vertical" /> : null}
                            {file2dUrl ? (
                                <Button onClick={open2dViewer} size="small" type="link">
                                    View 2D
                                </Button>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            }
            extra={
                <div className="otf-checkout-card-extra">
                    <Text className="otf-checkout-card-part-label">{`PART ${partNumber} OF ${partCount}`}</Text>
                    <Button
                        danger
                        onClick={() => setConfirmRemovePartVisible(true)}
                        size="small"
                        type="link"
                    >
                        Remove Part
                    </Button>
                </div>
            }
        >
            <div className="otf-checkout-variant-list">
                {variants.map((variant, index) => (
                    <CartItem
                        key={variant.id}
                        group={variant}
                        variantIndex={index + 1}
                        isAutoQuote={isAutoQuote}
                        onEditPartSpecifications={onEditPartSpecifications}
                    />
                ))}
            </div>

            <ThreeDViewerModal
                loading={threeDLoading}
                onClose={() => {
                    setThreeDViewerVisible(false);
                    setThreeDUrl(null);
                }}
                url={threeDUrl}
                visible={threeDViewerVisible}
            />

            <Modal
                open={confirmRemovePartVisible}
                onCancel={() => setConfirmRemovePartVisible(false)}
                onOk={handleRemovePart}
                okText="Remove"
                okButtonProps={{ danger: true, loading: removingPart }}
                centered
                closable={false}
                width={400}
            >
                <Typography.Title level={4}>Remove Item From Quote?</Typography.Title>
            </Modal>
        </Card>
    );
};
